/**
 * BESTIX v5.0 — HomePage
 * BESPOKIAN luxury editorial landing page - powered by Bespokian Events API
 */

import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Hero } from '@/components/hero/Hero';
import { EventCard, EventCardGrid, type EventCardProps } from '@/components/events/EventCard';
import { CategoryCard, CategoryCardGrid, type CategoryCardProps } from '@/components/events/CategoryCard';
import { SectionHeader, GuaranteeBanner } from '@/components/ui';
import { ValueProposition } from '@/components/sections/ValueProposition';
import { HowItWorks } from '@/components/sections/HowItWorks';
import { LastMinuteCard, type LastMinuteCardProps } from '@/components/deals';
import { RewardsBanner } from '@/components/rewards';
import { VIPSection, type VIPPackageProps } from '@/components/vip';
import { useEvents } from '@/queries/events.queries';
import { categoryThumbImages, fallbackEventImage } from '@/config/hero-images';
import type { Event } from '@/lib/api';

// ============================================
// VIP Package Data
// ============================================

const vipPackages: VIPPackageProps[] = [
  {
    id: 'vip-1',
    name: 'Premium Suite Experience',
    description: 'Private suite with catering, dedicated server, and VIP parking included.',
    price: 45000,
    originalPrice: 55000,
    image: 'https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?w=600&q=80',
    includes: ['Private Suite Access', 'Gourmet Catering', 'Premium Open Bar', 'VIP Parking', 'Dedicated Server'],
    tier: 'platinum',
  },
  {
    id: 'vip-2',
    name: 'Backstage Meet & Greet',
    description: 'Exclusive backstage access with artist meet and greet opportunity.',
    price: 75000,
    image: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=600&q=80',
    includes: ['Backstage Tour', 'Artist Meet & Greet', 'Signed Merchandise', 'Photo Opportunity', 'Floor Seats'],
    tier: 'diamond',
  },
  {
    id: 'vip-3',
    name: 'Gold Club Access',
    description: 'Premium club level seating with lounge access and complimentary drinks.',
    price: 28000,
    originalPrice: 35000,
    image: 'https://images.unsplash.com/photo-1507676184212-d03ab07a01bf?w=600&q=80',
    includes: ['Club Level Seats', 'Lounge Access', 'Complimentary Drinks', 'Early Entry'],
    tier: 'gold',
  },
];

// ============================================
// Hero Content
// ============================================

const heroContent = {
  title: 'Extraordinary Experiences,',
  subtitle: 'Curated for You',
  searchLabel: 'WHERE WOULD YOU LIKE TO GO?',
  searchPlaceholder: 'Search events, artists, venues...',
  backgroundImage: 'https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?w=1920&q=80',
};

// ============================================
// Category Data
// ============================================

const mainCategories: CategoryCardProps[] = [
  {
    name: 'Concerts',
    eventCount: 245,
    imageUrl: categoryThumbImages.concerts,
    href: '/events?category=concerts',
    description: 'Live music from the world\'s biggest artists',
  },
  {
    name: 'Sports',
    eventCount: 189,
    imageUrl: categoryThumbImages.sports,
    href: '/events?category=sports',
    description: 'Premium seats for legendary games',
  },
  {
    name: 'Theater',
    eventCount: 87,
    imageUrl: categoryThumbImages.theater,
    href: '/events?category=theater',
    description: 'Broadway, West End, and beyond',
  },
  {
    name: 'Festivals',
    eventCount: 42,
    imageUrl: categoryThumbImages.festivals,
    href: '/events?category=festivals',
    description: 'Multi-day experiences and exclusive access',
  },
];

// ============================================
// Transform API Data to Card Props
// ============================================

// Fallback images by category
const categoryImages: Record<string, string> = {
  concerts: 'https://images.unsplash.com/photo-1540039155733-5bb30b53aa14?w=600&q=80',
  sports: 'https://images.unsplash.com/photo-1461896836934-28e4b8f0e7ca?w=600&q=80',
  theater: 'https://images.unsplash.com/photo-1507676184212-d03ab07a01bf?w=600&q=80',
  festivals: 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=600&q=80',
  default: 'https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?w=600&q=80',
};

function getCategoryImage(category?: string): string {
  if (!category) return categoryImages.default;
  const lower = category.toLowerCase();
  if (lower.includes('concert') || lower.includes('music')) return categoryImages.concerts;
  if (lower.includes('sport') || lower.includes('nba') || lower.includes('nfl') || lower.includes('mlb')) return categoryImages.sports;
  if (lower.includes('theater') || lower.includes('broadway')) return categoryImages.theater;
  if (lower.includes('festival')) return categoryImages.festivals;
  return categoryImages.default;
}

// Parse date and time strings into a displayable format
function parseEventDateTime(dateStr: string, timeStr: string): string {
  try {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const formattedDate = date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
    return `${formattedDate} · ${timeStr}`;
  } catch {
    return `${dateStr} · ${timeStr}`;
  }
}

// Create an ISO datetime string from date and time (for countdown timer)
function createISODateTime(dateStr: string, timeStr: string): string {
  try {
    // Parse date
    const [year, month, day] = dateStr.split('-').map(Number);
    
    // Parse time (e.g., "7:00 PM" or "TBA")
    let hours = 20; // Default to 8 PM
    let minutes = 0;
    
    if (timeStr && timeStr !== 'TBA') {
      const timeMatch = timeStr.match(/(\d+):(\d+)\s*(AM|PM)?/i);
      if (timeMatch) {
        hours = parseInt(timeMatch[1], 10);
        minutes = parseInt(timeMatch[2], 10);
        if (timeMatch[3]?.toUpperCase() === 'PM' && hours < 12) hours += 12;
        if (timeMatch[3]?.toUpperCase() === 'AM' && hours === 12) hours = 0;
      }
    }
    
    const date = new Date(year, month - 1, day, hours, minutes);
    return date.toISOString();
  } catch {
    return new Date().toISOString();
  }
}

function transformEventToCardProps(event: Event): EventCardProps {
  return {
    id: event.id,
    title: event.name,
    subtitle: event.performers?.[0]?.name !== event.name ? event.performers?.[0]?.name : undefined,
    venue: event.venue.name,
    location: [event.venue.city, event.venue.state].filter(Boolean).join(', ') || 'Various Locations',
    dateTime: parseEventDateTime(event.date, event.time),
    priceFrom: event.priceRange ? Math.round(event.priceRange.min * 100) : 0,
    ticketsAvailable: event.ticketsAvailable ? 100 : 0,
    heatLevel: (event.ticketsAvailable ? 4 : 2) as 1 | 2 | 3 | 4 | 5,
    imageUrl: event.imageUrl || getCategoryImage(event.category),
    category: (event.category?.toUpperCase() || 'EVENT') as EventCardProps['category'],
  };
}

function transformEventToLastMinute(event: Event): LastMinuteCardProps {
  const minPrice = event.priceRange?.min || 100;
  const maxPrice = event.priceRange?.max || minPrice * 2;
  
  // Create a proper date string for the countdown
  // Parse time like "7:00 PM" into 24h format
  let hours = 20; // default 8pm
  let minutes = 0;
  const timeMatch = event.time.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
  if (timeMatch) {
    hours = parseInt(timeMatch[1], 10);
    minutes = parseInt(timeMatch[2], 10);
    const isPM = timeMatch[3]?.toUpperCase() === 'PM';
    if (isPM && hours < 12) hours += 12;
    if (!isPM && hours === 12) hours = 0;
  }
  const eventDateTimeISO = `${event.date}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`;
  
  return {
    id: String(event.id),
    title: event.name,
    venue: event.venue.name,
    location: [event.venue.city, event.venue.state].filter(Boolean).join(', ') || 'Various Locations',
    eventDateTime: eventDateTimeISO,
    image: event.imageUrl || getCategoryImage(event.category),
    originalPrice: Math.round(maxPrice * 100),
    dealPrice: Math.round(minPrice * 100 * 0.7), // 30% off
    seatsAvailable: 8,
    section: 'Premium',
  };
}

// ============================================
// Loading Skeleton
// ============================================

function EventGridSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="animate-pulse rounded-xl bg-gray-100" style={{ height: '400px' }} />
      ))}
    </div>
  );
}

// ============================================
// Tonight's Deals Section
// ============================================

function TonightDeals({ deals }: { deals: LastMinuteCardProps[] }) {
  if (deals.length === 0) return null;

  return (
    <section className="py-16 bg-[var(--color-navy)]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between mb-10">
          <div>
            <div className="flex items-center gap-2 text-[var(--color-coral)] mb-2">
              <span className="text-sm font-medium uppercase tracking-wide">Limited Time</span>
            </div>
            <h2 className="font-playfair italic text-3xl text-white">Tonight's Hot Deals</h2>
            <p className="text-white/60 mt-1">Last-minute tickets at incredible prices</p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {deals.slice(0, 3).map((deal) => (
            <LastMinuteCard key={deal.id} {...deal} />
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================
// Newsletter Section
// ============================================

function NewsletterSignup() {
  return (
    <section className="bg-[var(--color-navy)] py-16 px-6">
      <div className="max-w-2xl mx-auto text-center">
        <div className="w-16 h-px bg-white/40 mx-auto mb-6" />
        <h2 className="font-playfair italic text-3xl md:text-4xl text-white !text-white mb-2">
          Never Miss an Event
        </h2>
        <p className="text-white/70 mb-8">
          Subscribe to get exclusive presale access, personalized recommendations,
          and special offers delivered to your inbox.
        </p>
        <form className="flex flex-col sm:flex-row gap-4 justify-center">
          <input
            type="email"
            placeholder="Enter your email"
            className="flex-1 sm:max-w-sm px-5 py-3 rounded-full bg-white/10 text-white placeholder-white/50 border border-white/20 focus:outline-none focus:border-[var(--color-gold)]"
            required
          />
          <button
            type="submit"
            className="px-8 py-3 bg-[var(--color-gold)] text-[var(--color-navy)] font-semibold rounded-full hover:bg-[var(--color-gold-light)] transition-colors"
          >
            Subscribe
          </button>
        </form>
        <p className="mt-4 text-sm text-white/50">By subscribing, you agree to our Privacy Policy.</p>
        <div className="w-16 h-px bg-white/40 mx-auto mt-6" />
      </div>
    </section>
  );
}

// ============================================
// Main Component
// ============================================

export default function HomePage() {
  // Fetch featured events from API
  const { data: eventsData, isLoading } = useEvents({ per_page: 6 });

  const featuredEvents = useMemo(() => {
    if (!eventsData?.events) return [];
    return eventsData.events.map(transformEventToCardProps);
  }, [eventsData?.events]);

  const tonightDeals = useMemo(() => {
    if (!eventsData?.events) return [];
    return eventsData.events.slice(0, 3).map(transformEventToLastMinute);
  }, [eventsData?.events]);

  return (
    <>
      <Helmet>
        <title>BESTIX by BESPOKIAN | Luxury Event Tickets</title>
        <meta name="description" content="Discover and purchase premium tickets for concerts, sports, theater, and exclusive events. BESTIX by BESPOKIAN - Where extraordinary experiences begin." />
      </Helmet>

      {/* Transparent header for hero */}
      <Header variant="transparent" />

      <main>
        {/* Hero Section */}
        <Hero
          title={heroContent.title}
          subtitle={heroContent.subtitle}
          backgroundImage={heroContent.backgroundImage}
          searchLabel={heroContent.searchLabel}
          searchPlaceholder={heroContent.searchPlaceholder}
        />

        {/* Compact Trust Banner */}
        <GuaranteeBanner variant="compact" />

        {/* Featured Events */}
        <section className="py-20 px-6 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <SectionHeader
              title="Handpicked experiences"
              subtitle="Curated for you"
              action={{ label: 'View All', href: '/events' }}
            />
            {isLoading ? (
              <EventGridSkeleton />
            ) : featuredEvents.length > 0 ? (
              <EventCardGrid events={featuredEvents} columns={3} />
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500">No events available at the moment</p>
              </div>
            )}
          </div>
        </section>

        {/* Tonight's Deals */}
        {!isLoading && tonightDeals.length > 0 && <TonightDeals deals={tonightDeals} />}

        {/* Categories */}
        <section className="py-20 px-6">
          <div className="max-w-7xl mx-auto">
            <SectionHeader
              title="Browse by Category"
              subtitle="Find your next unforgettable experience"
            />
            <CategoryCardGrid categories={mainCategories} />
          </div>
        </section>

        {/* Value Proposition */}
        <ValueProposition variant="light" />

        {/* How It Works */}
        <HowItWorks />

        {/* VIP Packages */}
        <VIPSection packages={vipPackages} className="bg-gray-50" />

        {/* Rewards Banner */}
        <RewardsBanner variant="full" />

        {/* Newsletter */}
        <NewsletterSignup />
      </main>

      <Footer />
    </>
  );
}
